import { IPropertiesService, RefreshMode } from '../IPropertiesService.js';
import { IProperty } from '../IProperty.js';
import { IDesignItem } from '../../../item/IDesignItem.js';
import { ValueType } from '../ValueType.js';
import { PropertiesHelper } from './PropertiesHelper.js';
import { BindingTarget } from '../../../item/BindingTarget.js';
import { IBinding } from '../../../item/IBinding.js';
import { PropertyType } from '../PropertyType.js';
import { NodeType } from '../../../item/NodeType.js';
import { IPropertyGroup } from '../IPropertyGroup.js';
import { newElementFromString } from '../../../helper/ElementHelper.js';
import { IContextMenuItem } from '../../../helper/contextMenu/IContextMenuItem.js';
import { appendCssImportant, splitCssImportant } from '../../../helper/CssImportant.js';

export abstract class AbstractPropertiesService implements IPropertiesService {

  constructor(recreateElementsOnPropertyChange?: boolean) {
    this._recreateElementsOnPropertyChange = recreateElementsOnPropertyChange;
  }

  protected _recreateElementsOnPropertyChange: boolean = false;

  private static _stylesCache = new Map<IDesignItem, Set<string>>;
  private _cssCacheClearTimer: NodeJS.Timeout;
  private static _bindingsCache = new Map<IDesignItem, IBinding[]>;
  private static _bindingsCacheClearTimer: NodeJS.Timeout;

  abstract getRefreshMode(designItem: IDesignItem): RefreshMode;

  abstract isHandledElement(designItem: IDesignItem): boolean;

  protected _notifyChangedProperty(designItem: IDesignItem, property: IProperty, value: any) {
  }

  async getProperty(designItem: IDesignItem, name: string): Promise<IProperty> {
    let properties = await this.getProperties(designItem);
    if ('properties' in properties[0]) {
      return (<IPropertyGroup[]>properties).flatMap(x => x.properties).find(x => x.name == name);
    }
    else
      return (<IProperty[]>properties).find(x => x.name == name);
  }

  abstract getProperties(designItem: IDesignItem): Promise<IProperty[] | IPropertyGroup[]>;

  async setValue(designItems: IDesignItem[], property: IProperty, value: any) {
    const cg = designItems[0].openGroup("property changed: " + property.name + " to " + value);
    for (let d of designItems) {
      if (!this.isHandledElement(d))
        continue;
      if (!this.getProperty(d, property.name))
        continue;

      if (property.propertyType == PropertyType.cssValue) {
        const parsedValue = typeof value === 'string' ? splitCssImportant(value) : { value, important: false };
        await d.updateStyleInSheetOrLocalAsync(property.propertyName ?? property.name, parsedValue.value, parsedValue.important);
        //TODO: fix this hack somehow
        //unkown css property names do not trigger the mutation observer of property grid, 
        //fixed by assinging style again to the attribute
        (<HTMLElement>d.element).setAttribute('style', (<HTMLElement>d.element).getAttribute('style'));
      } else {
        let attributeName = property.attributeName
        if (!attributeName)
          attributeName = PropertiesHelper.camelToDashCase(property.name);


        if (property.type === 'object') {
          const json = JSON.stringify(value);
          if (property.propertyType == PropertyType.propertyAndAttribute)
            d.setPropertyAndAttribute(property.name, json);
          else if (property.propertyType == PropertyType.attribute)
            d.setAttribute(attributeName, json);
          else if (property.propertyType == PropertyType.property)
            d.setProperty(property.name, value);
        } else if (property.type == 'boolean' && (value === false || value == null)) {
          if (property.propertyType == PropertyType.attribute || property.propertyType == PropertyType.propertyAndAttribute)
            d.removeAttribute(attributeName);
          if (property.propertyType == PropertyType.property || property.propertyType == PropertyType.propertyAndAttribute)
            d.setProperty(property.name, false);
        }
        else if (property.type == 'boolean' && value === true) {
          if (property.propertyType == PropertyType.attribute || property.propertyType == PropertyType.propertyAndAttribute)
            d.setAttribute(attributeName, "");
          if (property.propertyType == PropertyType.property || property.propertyType == PropertyType.propertyAndAttribute)
            d.setProperty(property.name, true);
        }
        else {
          if (property.propertyType == PropertyType.propertyAndAttribute)
            d.setPropertyAndAttribute(property.name, value);
          else if (property.propertyType == PropertyType.attribute)
            d.setAttribute(attributeName, value.toString());
          else if (property.propertyType == PropertyType.property)
            d.setProperty(property.name, value);
        }
      }
      this._notifyChangedProperty(d, property, value);
    }
    cg.commit();
    if (this._recreateElementsOnPropertyChange)
      AbstractPropertiesService.recreateElements(this, designItems);
  }

  getPropertyTarget(designItem: IDesignItem, property: IProperty): BindingTarget {
    if (property.propertyType == PropertyType.attribute)
      return BindingTarget.attribute;
    if (property.propertyType == PropertyType.cssValue)
      return BindingTarget.css;
    return BindingTarget.property;
  }

  clearValue(designItems: IDesignItem[], property: IProperty, clearType: 'all' | 'binding' | 'value') {
    const cg = designItems[0].openGroup("property cleared: " + property.name);
    for (let d of designItems) {
      if (clearType != 'binding') {
        if (property.propertyType == PropertyType.cssValue) {
          d.removeStyle(property.propertyName ?? property.name);
        } else {
          if (property.propertyType == PropertyType.property || property.propertyType == PropertyType.propertyAndAttribute) {
            d.element[property.propertyName ?? property.name] = null;
          }
          if (property.propertyType == PropertyType.attribute || property.propertyType == PropertyType.propertyAndAttribute) {
            let attributeName = property.attributeName
            if (!attributeName)
              attributeName = PropertiesHelper.camelToDashCase(property.name);
            d.removeAttribute(attributeName);
          }
        }
      }
      if (clearType != 'value') {
        d.serviceContainer.forSomeServicesTillResult('bindingService', (s) => {
          return s.clearBinding(d, property.name, this.getPropertyTarget(d, property));
        });
      }
      this._notifyChangedProperty(d, property, undefined);
    }
    cg.commit();
    if (this._recreateElementsOnPropertyChange)
      AbstractPropertiesService.recreateElements(this, designItems);
  }

  isSet(designItems: IDesignItem[], property: IProperty): ValueType {
    let all = true;
    let some = false;
    if (designItems != null && designItems.length !== 0) {
      let attributeName = property.attributeName
      if (!attributeName)
        attributeName = PropertiesHelper.camelToDashCase(property.name);
      for (let x of designItems) {
        let has = false;
        if (property.propertyType == PropertyType.cssValue)
          has = x.hasStyle(property.name);
        else
          has = x.hasAttribute(attributeName);
        all = all && has;
        some = some || has;
        if (!all && some)
          break;
      };

      const bindings = AbstractPropertiesService.getOrBuildCachedBindings(designItems[0]);
      if (property.propertyType == PropertyType.cssValue) {
        if (bindings && bindings.find(x => (x.target == BindingTarget.css || x.target == BindingTarget.cssvar) && x.targetName == property.name))
          return ValueType.bound;
      } else {
        if (property.propertyType == PropertyType.attribute) {
          if (bindings && bindings.find(x => x.target == BindingTarget.attribute && x.targetName == property.name))
            return ValueType.bound;
        } else if (property.propertyType == PropertyType.property) {
          if (bindings && bindings.find(x => (x.target == BindingTarget.property || x.target == BindingTarget.explicitProperty) && x.targetName == property.name))
            return ValueType.bound;
        } else {
          if (bindings && bindings.find(x => (x.target == BindingTarget.property || x.target == BindingTarget.explicitProperty || x.target == BindingTarget.attribute) && x.targetName == property.name))
            return ValueType.bound;
        }
      }

      if (!all && property.propertyType == PropertyType.cssValue) {
        let styles = AbstractPropertiesService._stylesCache.get(designItems[0]);
        if (!styles) {
          styles = new Set(designItems[0].getAllStyles().filter(x => x.selector != null).flatMap(x => x.declarations).map(x => x.name));
          AbstractPropertiesService._stylesCache.set(designItems[0], styles);
          clearTimeout(this._cssCacheClearTimer);
          this._cssCacheClearTimer = setTimeout(() => AbstractPropertiesService._stylesCache.clear(), 30);
        }

        let cssValue = styles.has(property.propertyName ?? property.name);
        if (cssValue)
          return ValueType.fromStylesheet;
      }
    }
    else
      return ValueType.none

    return all ? ValueType.all : some ? ValueType.some : ValueType.none;
  }

  static getOrBuildCachedBindings(designItem: IDesignItem) {
    let bindings = AbstractPropertiesService._bindingsCache.get(designItem);
    if (!bindings) {
      const services = designItem.serviceContainer.getServices('bindingService');
      bindings = [];
      if (services) {
        for (const s of services) {
          const bs = s.getBindings(designItem);
          if (bs && bs.length > 0) {
            bindings.push(...bs);
          }
        }
      }
      AbstractPropertiesService._bindingsCache.set(designItem, bindings);
      clearTimeout(this._bindingsCacheClearTimer);
      this._bindingsCacheClearTimer = setTimeout(() => AbstractPropertiesService._bindingsCache.clear(), 30);
    }
    return bindings;
  }

  getValue(designItems: IDesignItem[], property: IProperty) {
    if (designItems != null && designItems.length !== 0) {
      if (property.propertyType == PropertyType.cssValue) {
        let lastValue = designItems[0].getStyle(property.propertyName ?? property.name);
        for (const d of designItems) {
          let value = d.getStyle(property.name);
          if (value != lastValue) {
            lastValue = null;
            break;
          }
        }
        return typeof lastValue === 'string' ? appendCssImportant(lastValue, designItems[0].isStyleImportant(property.propertyName ?? property.name)) : lastValue;
      } else if (property.propertyType == PropertyType.property) {
        let propertyName = property.propertyName
        if (!propertyName)
          propertyName = PropertiesHelper.dashToCamelCase(property.name);
        return designItems[0].element[propertyName];
      } else {
        let attributeName = property.attributeName
        if (!attributeName)
          attributeName = PropertiesHelper.camelToDashCase(property.name);

        if (property.type == 'boolean') {
          if (designItems[0].hasAttribute(attributeName)) {
            const val = designItems[0].getAttribute(attributeName);
            if (val == "")
              return true;
            return val;
          }
          return false;
        }
        let lastValue = designItems[0].getAttribute(attributeName);
        /*
        for (const x of designItems) {
          let value = x.attributes.get(attributeName);
          if (value != lastValue) {
            lastValue = null;
            break;
          }
        }
        */
        return lastValue;
      }
    }
    return null;
  }

  getBinding(designItems: IDesignItem[], property: IProperty): IBinding {
    const bindings = AbstractPropertiesService.getOrBuildCachedBindings(designItems[0]);
    if (bindings != null) {
      if (property.propertyType == PropertyType.cssValue) {
        return bindings.find(x => (x.target == BindingTarget.css || x.target == BindingTarget.cssvar) && x.targetName == property.name);
      } else {
        if (property.propertyType == PropertyType.attribute) {
          return bindings.find(x => x.target == BindingTarget.attribute && x.targetName == property.name);
        } else if (property.propertyType == PropertyType.property) {
          return bindings.find(x => (x.target == BindingTarget.property || x.target == BindingTarget.explicitProperty) && x.targetName == property.name);
        } else {
          return bindings.find(x => (x.target == BindingTarget.property || x.target == BindingTarget.explicitProperty || x.target == BindingTarget.attribute) && x.targetName == property.name);
        }
      }
    }
    return null;
  }

  getUnsetValue(designItems: IDesignItem[], property: IProperty) {
    if (property.propertyType == PropertyType.cssValue) {
      if (designItems != null && designItems.length !== 0) {
        if (designItems[0].nodeType == NodeType.Element) {
          let v = window.getComputedStyle(designItems[0].element)[property.propertyName ?? property.name];
          return v;
        }
      }
      return null;
    }
    else
      return property.defaultValue;
  }

  protected static recreateElements(service: IPropertiesService, designItems: IDesignItem[]) {
    for (let d of designItems) {
      if (!service.isHandledElement(d))
        continue;

      let txt = '<' + d.name + ' ';
      for (let a of d.element.attributes) {
        txt += a.name + '="' + a.value.replaceAll('"', '&quot;') + '" ';
      }
      txt += '></' + d.name + '>';
      let element = newElementFromString(txt, designItems[0].document); // some custom elements only parse attributes during constructor call 
      for (let c of [...d.element.childNodes])
        element.appendChild(c);
      (<HTMLElement>element).style.pointerEvents = 'auto';
      (<HTMLElement>d.node).insertAdjacentElement('beforebegin', element);
      if (d.node.parentNode)
        (<HTMLElement>d.node.parentNode).removeChild(d.node);
      d.replaceNode(element);
    }
  }

  public getContextMenu(designItems: IDesignItem[], property: IProperty): IContextMenuItem[] {
    const ctxMenuItems: IContextMenuItem[] = [
      {
        title: 'clear', action: (e) => {
          property.service.clearValue(designItems, property, 'value');
          designItems[0].instanceServiceContainer.refreshExtensions(designItems);
        }
      },
      {
        title: 'edit as text', action: (e, _1, _2, menu) => {
          menu.close();
          setTimeout(async () => {
            const oldValue = property.service.getValue(designItems, property);
            let value = prompt(`edit value of '${property.name}' as string:`, oldValue);
            if (value && value != oldValue) {
              await property.service.setValue(designItems, property, value);
            }
            designItems[0].instanceServiceContainer.refreshExtensions(designItems);
          }, 10)
        }
      },
    ];
    if (designItems[0].serviceContainer.config.openBindingsEditor) {
      ctxMenuItems.push(...[
        { title: '-' },
        {
          title: 'edit binding', action: () => {
            let target = property.service.getPropertyTarget(designItems[0], property);
            let binding = property.service.getBinding(designItems, property);
            designItems[0].serviceContainer.config.openBindingsEditor(property, designItems, binding, target);
          }
        }
      ]);
      if (property.service.isSet(designItems, property) == ValueType.bound) {
        ctxMenuItems.push(...[
          {
            title: 'clear binding', action: () => {
              property.service.clearValue(designItems, property, 'binding');
              designItems[0].instanceServiceContainer.refreshExtensions(designItems);
            }
          }
        ]);
      }
    };
    return ctxMenuItems;
  }
}
